import React from 'react';
import { Box, Paper, Typography, Chip, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getServiceById, ServiceConfig } from '../../config/services.config';
import ServiceUserIndicator from './ServiceUserIndicator';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';

interface ServiceHeaderProps {
  serviceId?: string;
  title?: string;
  subtitle?: string;
  showServiceInfo?: boolean;
  showUserIndicator?: boolean;
  children?: React.ReactNode;
}

const ServiceHeader: React.FC<ServiceHeaderProps> = ({
  serviceId,
  title,
  subtitle,
  showServiceInfo = true,
  showUserIndicator = true,
  children
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const auth = useUnifiedAuth();
  
  // Get service config - prioritize explicit serviceId
  let currentService: ServiceConfig | null = null;
  
  if (serviceId) {
    currentService = getServiceById(serviceId) || null;
  } else {
    // Fallback to path detection if no serviceId provided
    const path = location.pathname;
    if (path.includes('/shuttle') || path.includes('shuttle-booking')) {
      currentService = getServiceById('shuttle') || null;
    } else if (path.includes('/equipment') || path.includes('/reminders')) {
      currentService = getServiceById('equipment') || null;
    } else if (path.includes('/marketplace')) {
      currentService = getServiceById('marketplace') || null;
    }
  }
  
  const ServiceIcon = currentService?.icon;
  
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {/* Top row with service info and user indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {/* Service Info */}
        <Box sx={{ flex: 1 }}>
          {title ? (
            <>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {ServiceIcon && <ServiceIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </>
          ) : currentService ? (
            <>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {ServiceIcon && <ServiceIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
                {t(currentService.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(currentService.description || '')}
              </Typography>
            </>
          ) : null}
        </Box>
        
        {/* User Indicator and Status */}
        {showUserIndicator && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            {/* User info with premium badge */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ServiceUserIndicator variant="compact" showEmail={false} />
                {/* Premium/Freemium Badge */}
                {auth.isServiceAuthenticated && (
                  <Chip
                    label={auth.serviceUser?.has_premium_access ? t('services.premiumUser') : t('services.freemiumUser', 'Freemium User')}
                    color={auth.serviceUser?.has_premium_access ? 'primary' : 'default'}
                    variant={auth.serviceUser?.has_premium_access ? 'filled' : 'outlined'}
                    size="small"
                  />
                )}
              </Box>
              {/* Email below */}
              {auth.email && (
                <Typography variant="caption" color="text.secondary">
                  {auth.email}
                </Typography>
              )}
            </Box>
            
            {/* Service-specific status chips */}
            {showServiceInfo && currentService && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {currentService.enabled && (
                  <Chip 
                    label={t('service.status.active')} 
                    color="success" 
                    size="small" 
                    variant="outlined"
                  />
                )}
                
                {currentService.category && (
                  <Chip 
                    label={t(`service.category.${currentService.category}`)} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                
                {currentService.requiresPremium && auth.userType === 'guest' && !auth.serviceUser?.has_premium_access && (
                  <Chip 
                    label={t('service.status.premiumRequired')} 
                    color="warning" 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      {/* Optional children (action buttons, etc.) */}
      {children && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {children}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ServiceHeader;